// Import legal documents as raw strings (Vite handles ?raw)
import tosRaw from "../../../../../../legal/tos.md?raw";
import ppRaw from "../../../../../../legal/privacy-policy.md?raw";

export interface LegalDocument {
  version: string;
  effectiveDate: string;
  changeSummary: string;
  html: string;
}

/** Parse YAML frontmatter from a markdown string. */
export function parseFrontmatter(raw: string): {
  meta: Record<string, string>;
  body: string;
} {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) return { meta: {}, body: raw };

  const meta: Record<string, string> = {};
  for (const line of match[1].split("\n")) {
    const idx = line.indexOf(":");
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    let value = line.slice(idx + 1).trim();
    // Strip surrounding quotes
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    meta[key] = value;
  }
  return { meta, body: match[2] };
}

/** Escape HTML special characters to prevent XSS in rendered legal docs. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Convert simple markdown (headings, paragraphs, lists) to HTML.
 * Handles ###, -, *, and paragraph breaks. No library needed for legal docs.
 */
function markdownToHtml(md: string): string {
  const lines = md.split("\n");
  const output: string[] = [];
  let inList = false;

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      if (inList) {
        output.push("</ul>");
        inList = false;
      }
      continue;
    }

    // Headings
    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      if (inList) {
        output.push("</ul>");
        inList = false;
      }
      const level = headingMatch[1].length;
      output.push(`<h${level}>${escapeHtml(headingMatch[2])}</h${level}>`);
      continue;
    }

    // List items
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      if (!inList) {
        output.push("<ul>");
        inList = true;
      }
      output.push(`<li>${escapeHtml(trimmed.slice(2))}</li>`);
      continue;
    }

    // Paragraph
    if (inList) {
      output.push("</ul>");
      inList = false;
    }
    output.push(`<p>${escapeHtml(trimmed)}</p>`);
  }

  if (inList) output.push("</ul>");
  return output.join("\n");
}

function parseLegalDoc(raw: string): LegalDocument {
  const { meta, body } = parseFrontmatter(raw);
  return {
    version: meta.version || "1.0",
    effectiveDate: meta.effective_date || "",
    changeSummary: meta.change_summary || "",
    html: markdownToHtml(body),
  };
}

export const tos: LegalDocument = parseLegalDoc(tosRaw);
export const privacyPolicy: LegalDocument = parseLegalDoc(ppRaw);

/** True when either document has a change summary (i.e., this is a version update). */
export const hasUpdatedTerms = !!(
  tos.changeSummary || privacyPolicy.changeSummary
);
